import { useTransition, useState } from "react";

import { Button } from "~/components/Button";
import { Icon } from "~/components/icon";
import { useCopyToClipboard } from "~/hooks/useCopyToClipboard";
import type { CustomerSearchResult } from "~/types";

import { CustomerPicker } from "./CustomerPicker";

interface ByocKitFormProps {
  onSuccess?: () => void;
}

type MessageType = "success" | "error" | null;

type KitChoice = "standard" | "managed-azure" | "managed-aws" | "managed-gcp";

const KIT_CHOICE_LABELS: Record<KitChoice, string> = {
  standard: "Standard",
  "managed-azure": "Managed (Azure)",
  "managed-aws": "Managed (AWS)",
  "managed-gcp": "Managed (GCP)",
};

type ManagedKind = Exclude<KitChoice, "standard">;

const MANAGED_KIND_INFO: Record<
  ManagedKind,
  {
    clusterDir: string;
    secretsCommand: string;
    secretsFile: string;
    bootstrapTfvars: string;
    bootstrapDir: string;
    initCommand: string;
    applyIdentity: string;
    tfvarsHint: string;
  }
> = {
  "managed-azure": {
    clusterDir: "cluster/",
    secretsCommand: "python3 scripts/generate-secrets.py",
    secretsFile: "cluster/values.secret.yaml",
    bootstrapTfvars: "bootstrap/turbopuffer.tfvars",
    bootstrapDir: "bootstrap/",
    initCommand: "terraform init",
    applyIdentity: "subscription Owner",
    tfvarsHint: "region, subscription ID, cluster name, storage names",
  },
  "managed-aws": {
    clusterDir: "aws/cluster/",
    secretsCommand: "python3 ../../scripts/generate-secrets.py",
    secretsFile: "aws/cluster/values.secret.yaml",
    bootstrapTfvars: "aws/bootstrap/turbopuffer.tfvars",
    bootstrapDir: "aws/bootstrap/",
    initCommand: "terraform init -backend-config=backend.hcl",
    applyIdentity: "account admin",
    tfvarsHint: "region, cluster name, storage names",
  },
  "managed-gcp": {
    clusterDir: "gcp/cluster/",
    secretsCommand: "python3 ../../scripts/generate-secrets.py",
    secretsFile: "gcp/cluster/values.secret.yaml",
    bootstrapTfvars: "gcp/bootstrap/turbopuffer.tfvars",
    bootstrapDir: "gcp/bootstrap/",
    initCommand: "terraform init -backend-config=backend.hcl",
    applyIdentity: "project Owner",
    tfvarsHint: "region, project id, cluster name, storage names",
  },
};

export function ByocKitForm({ onSuccess }: ByocKitFormProps) {
  const [isPending, startTransition] = useTransition();
  const [message, setMessage] = useState<{
    type: MessageType;
    text: string;
  } | null>(null);
  const [generatedCommand, setGeneratedCommand] = useState<string | null>(null);
  const [generatedKind, setGeneratedKind] = useState<KitChoice>("standard");
  const [copiedText, copy, resetCopiedText] = useCopyToClipboard();
  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerSearchResult | null>(null);
  const [kind, setKind] = useState<KitChoice>("standard");
  const [teleportJoinToken, setTeleportJoinToken] = useState("");
  const [vendorHelmChart, setVendorHelmChart] = useState(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedCustomer) return;
    const customerId = selectedCustomer.id;
    setMessage(null);
    setGeneratedCommand(null);

    startTransition(() => {
      (async () => {
        try {
          // Create an API key for the customer
          const apiKeyResponse = await fetch(
            `/api/customers/${customerId}/cluster_api_key`,
            {
              method: "POST",
              credentials: "include",
            },
          );

          if (!apiKeyResponse.ok) {
            const errorData = await apiKeyResponse.json().catch(() => ({}));
            setMessage({
              type: "error",
              text:
                errorData.message || "Failed to create API key for customer",
            });
            return;
          }

          const apiKeyData = await apiKeyResponse.json();
          const apiKey = apiKeyData.api_key;
          const responseCustomerId = apiKeyData.customer_id;
          const customerName = apiKeyData.customer_name;

          // Construct the BYOC kit URL using customer_id as the identifier.
          const baseUrl = window.location.origin;
          const urlParams = new URLSearchParams();
          urlParams.append("customer_id", responseCustomerId);
          if (kind !== "standard") {
            urlParams.append("offering", "managed");
            urlParams.append("cloud", kind.replace("managed-", ""));
            urlParams.append("teleport_join_token", teleportJoinToken.trim());
          }
          if (kind === "standard" && vendorHelmChart) {
            urlParams.append("vendor_helm_chart", "true");
          }
          const kitUrl = `${baseUrl}/api/generate-byoc-kit?${urlParams.toString()}`;

          // Local download filename uses the slugified customer name so the
          // tar isn't named by an opaque ULID.
          const slug = customerName
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "");
          const suffix = kind !== "standard" ? "byoc-managed-kit" : "byoc-kit";
          const filename = `${slug || responseCustomerId}-${suffix}.tar`;
          const curlCommand = `curl -f -H "Authorization: Bearer ${apiKey}" -o "${filename}" "${kitUrl}"`;

          setGeneratedCommand(curlCommand);
          setGeneratedKind(kind);
          setMessage({
            type: "success",
            text: `API Key created for customer ${customerName}! Share this command with ${customerName} to download their BYOC kit.`,
          });
          onSuccess?.();
        } catch (_error) {
          setMessage({ type: "error", text: "Network error occurred" });
        }
      })();
    });
  };

  const handleCopyCommand = async (command: string) => {
    const success = await copy(command);
    if (success) {
      setTimeout(() => {
        resetCopiedText();
      }, 2000);
    }
  };

  const CommandCopyDisplay = ({
    command,
    label,
  }: {
    command: string;
    label: string;
  }) => (
    <div>
      <label className="mb-2 block text-sm">{label}:</label>
      <div className="group relative mb-2">
        <div
          className="cursor-pointer space-y-1 break-all rounded bg-gray-100 p-2 pr-10 font-mono text-xs text-gray-600 transition-colors hover:bg-gray-200"
          onClick={() => handleCopyCommand(command)}
        >
          {command}
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleCopyCommand(command);
          }}
          className="absolute right-2 top-2 rounded p-1 transition-colors hover:bg-gray-200"
          title={copiedText === command ? "Copied!" : "Copy command"}
        >
          <Icon
            name={copiedText === command ? "Check" : "Copy"}
            size={14}
            className={
              copiedText === command
                ? "text-blue-600"
                : "text-gray-600 hover:text-gray-800"
            }
          />
        </button>
      </div>
    </div>
  );

  return (
    <form method="post" className="space-y-4" onSubmit={handleSubmit}>
      {message ? (
        <div
          className={`rounded-md p-3 ${
            message.type === "error"
              ? "bg-red-50 text-red-800"
              : "bg-blue-50 text-blue-800"
          }`}
        >
          {message.text}
        </div>
      ) : null}

      {!generatedCommand ? (
        <>
          <div>
            <label htmlFor="customer_id" className="mb-1 block text-sm">
              Customer
            </label>
            <CustomerPicker
              id="customer_id"
              disabled={isPending}
              value={selectedCustomer}
              onChange={setSelectedCustomer}
            />
            <p className="mt-1 text-xs text-gray-500">
              Don&apos;t see the customer?{" "}
              <a
                href="https://turbopuffer.com/dashboard/admin/customers"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline hover:text-blue-800"
              >
                Create one in the dashboard
              </a>
              .
            </p>
          </div>

          <fieldset disabled={isPending}>
            <legend className="mb-1 block text-sm">Kit type</legend>
            <div className="flex gap-4">
              {(
                [
                  "standard",
                  "managed-azure",
                  "managed-aws",
                  "managed-gcp",
                ] as const
              ).map((value) => (
                <label key={value} className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="kind"
                    value={value}
                    checked={kind === value}
                    onChange={() => {
                      setKind(value);
                      setGeneratedCommand(null);
                    }}
                  />
                  {KIT_CHOICE_LABELS[value]}
                </label>
              ))}
            </div>
          </fieldset>

          {kind === "standard" ? (
            <div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  name="vendor_helm_chart"
                  checked={vendorHelmChart}
                  disabled={isPending}
                  onChange={(e) => {
                    setVendorHelmChart(e.target.checked);
                    setGeneratedCommand(null);
                  }}
                />
                Include Helm chart
              </label>
              <p className="mt-1 text-xs text-gray-500">
                Vendor the Helm chart in the kit for air-gapped deployments
              </p>
            </div>
          ) : null}

          {kind !== "standard" ? (
            <div>
              <label
                htmlFor="teleport_join_token"
                className="mb-1 block text-sm"
              >
                Teleport join token
              </label>
              <input
                type="text"
                id="teleport_join_token"
                value={teleportJoinToken}
                onChange={(e) => setTeleportJoinToken(e.target.value)}
                disabled={isPending}
                required
                placeholder="byoc-jumphost-<customer>"
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                The Teleport provision-token name for this cluster (provided by
                turbopuffer). Baked into the kit&apos;s bootstrap config.
              </p>
            </div>
          ) : null}

          <Button
            type="submit"
            variant="outline"
            className="w-full rounded-md bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-300 disabled:text-white"
            disabled={
              isPending ||
              !selectedCustomer ||
              (kind !== "standard" && teleportJoinToken.trim() === "")
            }
          >
            {isPending ? "Generating..." : "Generate BYOC Kit Download Command"}
          </Button>
        </>
      ) : null}

      {generatedCommand ? (
        <div className="mt-4 space-y-4">
          <CommandCopyDisplay
            command={generatedCommand}
            label="Secure Download Command"
          />

          <div className="mt-6 rounded-md bg-amber-50 p-4">
            <h3 className="mb-3 text-sm font-medium text-amber-900">
              {generatedKind !== "standard"
                ? "Tell the customer to:"
                : "Before giving the kit to the customer:"}
            </h3>
            {generatedKind !== "standard" ? (
              (() => {
                const info = MANAGED_KIND_INFO[generatedKind];
                return (
                  <ol className="list-decimal space-y-2 pl-5 text-sm text-amber-800">
                    <li>
                      Unpack the tar, then from the{" "}
                      <span className="font-mono text-xs">
                        {info.clusterDir}
                      </span>{" "}
                      directory run{" "}
                      <span className="font-mono text-xs">
                        {info.secretsCommand}
                      </span>{" "}
                      to write{" "}
                      <span className="font-mono text-xs">
                        {info.secretsFile}
                      </span>{" "}
                      (the plaintext data-plane key is shown only to them).
                    </li>
                    <li>
                      Fill in{" "}
                      <span className="font-mono text-xs">
                        {info.bootstrapTfvars}
                      </span>{" "}
                      ({info.tfvarsHint}), then from{" "}
                      <span className="font-mono text-xs">
                        {info.bootstrapDir}
                      </span>{" "}
                      run as {info.applyIdentity}:
                      <pre className="mt-1 rounded bg-amber-100 p-2 font-mono text-xs">
                        {`${info.initCommand}\nterraform apply -var-file=turbopuffer.tfvars`}
                      </pre>
                      and open the install window.
                    </li>
                  </ol>
                );
              })()
            ) : (
              <div className="space-y-3 text-sm text-amber-800">
                <div>
                  <span className="font-medium">AWS:</span> Update the{" "}
                  <a
                    href="https://github.com/turbopuffer/turbopuffer/tree/master/config/terraform/image-registries"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline hover:text-blue-800"
                  >
                    image registries configuration
                  </a>{" "}
                  so that they can access our private repositories.
                </div>
                <div>
                  <span className="font-medium">GCP:</span> Modify the
                  permissions for the charts and turbopuffer artifact registries
                  by adding the domain and their service account email{" "}
                  <a
                    href="https://console.cloud.google.com/iam-admin/iam?referrer=search&project=turbopuffer-onprem&inv=1&invt=Ab0TGg"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline hover:text-blue-800"
                  >
                    here
                  </a>
                  , granting both the domain and the service account the{" "}
                  <span className="font-mono text-xs">
                    Artifact Registry Reader
                  </span>{" "}
                  role.
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </form>
  );
}
